/**
 * Rich text core, shared by server, public site and admin. Never imports Tiptap.
 * A rich field holds either legacy plain text or a Tiptap document serialised as JSON.
 */

export type RtMark = { type: string; attrs?: Record<string, unknown> };
export type RtNode = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RtNode[];
  marks?: RtMark[];
  text?: string;
};
export type RtDoc = RtNode & { type: "doc" };

export type Profiel = "kort" | "pagina" | "artikel";

const KORT_NODES = ["doc", "paragraph", "text", "hardBreak"];
const PAGINA_NODES = [...KORT_NODES, "bulletList", "orderedList", "listItem", "blockquote"];
const ARTIKEL_NODES = [
  ...PAGINA_NODES,
  "heading",
  "horizontalRule",
  "table",
  "tableRow",
  "tableHeader",
  "tableCell",
  "image",
  "youtube",
  "details",
  "detailsSummary",
  "detailsContent",
];
const MARKS = ["bold", "italic", "link", "superscript", "subscript"];

export const PROFIELEN: Record<Profiel, { nodes: Set<string>; marks: Set<string> }> = {
  kort: { nodes: new Set(KORT_NODES), marks: new Set(MARKS) },
  pagina: { nodes: new Set(PAGINA_NODES), marks: new Set(MARKS) },
  artikel: { nodes: new Set(ARTIKEL_NODES), marks: new Set(MARKS) },
};

const DOC_PREFIX = '{"type":"doc"';

export function isDocString(waarde: string): boolean {
  return waarde.trimStart().startsWith(DOC_PREFIX);
}

function plainToDoc(tekst: string): RtDoc {
  const alineas = tekst
    .split(/\n\s*\n/)
    .map((deel) => deel.trim())
    .filter((deel) => deel !== "");
  if (alineas.length === 0) return { type: "doc", content: [{ type: "paragraph" }] };
  return {
    type: "doc",
    content: alineas.map((alinea) => {
      const content: RtNode[] = [];
      alinea.split("\n").forEach((regel, index) => {
        if (index > 0) content.push({ type: "hardBreak" });
        if (regel !== "") content.push({ type: "text", text: regel });
      });
      return { type: "paragraph", content };
    }),
  };
}

/** Reads legacy plain text or a JSON document into a document. */
export function leesRijkeTekst(waarde: string | null | undefined): RtDoc {
  const tekst = waarde ?? "";
  if (isDocString(tekst)) {
    try {
      const parsed = JSON.parse(tekst) as unknown;
      if (typeof parsed === "object" && parsed !== null && (parsed as RtNode).type === "doc") {
        return parsed as RtDoc;
      }
    } catch {
      // Invalid JSON: show it as plain text rather than failing.
    }
  }
  return plainToDoc(tekst);
}

const INHOUD_NODES = new Set(["image", "youtube", "horizontalRule", "table"]);

export function isLeeg(node: RtNode): boolean {
  if (node.type === "text") return (node.text ?? "").trim() === "";
  if (INHOUD_NODES.has(node.type)) return false;
  return (node.content ?? []).every(isLeeg);
}

/** Empty document becomes "", so the fallback to the code text keeps working. */
export function schrijfRijkeTekst(doc: RtDoc): string {
  return isLeeg(doc) ? "" : JSON.stringify(doc);
}

export function hrefToegelaten(href: unknown): href is string {
  if (typeof href !== "string") return false;
  const h = href.trim();
  return (
    (h.startsWith("/") && !h.startsWith("//")) ||
    h.startsWith("https://") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  );
}

export function youtubeId(src: unknown): string | null {
  if (typeof src !== "string") return null;
  const match = src.match(/(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return match?.[1] ?? null;
}

/** Validates a document against a profile. Returns readable Dutch error messages. */
export function valideerDoc(doc: RtNode, profiel: Profiel): string[] {
  const regels = PROFIELEN[profiel];
  const fouten = new Set<string>();
  const loop = (node: RtNode) => {
    if (!regels.nodes.has(node.type)) {
      fouten.add(`Het onderdeel "${node.type}" is hier niet toegelaten.`);
      return;
    }
    if (node.type === "heading" && node.attrs?.["level"] !== 3) fouten.add("Alleen tussenkoppen van niveau 3 zijn toegelaten.");
    if ((node.type === "tableCell" || node.type === "tableHeader") &&
      (Number(node.attrs?.["colspan"] ?? 1) !== 1 || Number(node.attrs?.["rowspan"] ?? 1) !== 1)) {
      fouten.add("Samengevoegde tabelcellen zijn niet toegelaten.");
    }
    if (node.type === "image") {
      if (!node.attrs?.["mediaId"] && !node.attrs?.["src"]) fouten.add("Een foto in de tekst mist haar bron.");
      if (typeof node.attrs?.["alt"] !== "string" || node.attrs["alt"].trim() === "") {
        fouten.add("Geef een beschrijving bij elke foto in de tekst.");
      }
    }
    if (node.type === "youtube") {
      if (!youtubeId(node.attrs?.["src"])) fouten.add("Een video in de tekst heeft geen geldig YouTube-adres.");
      if (typeof node.attrs?.["title"] !== "string" || node.attrs["title"].trim() === "") {
        fouten.add("Geef een titel bij elke video in de tekst.");
      }
    }
    for (const mark of node.marks ?? []) {
      if (!regels.marks.has(mark.type)) fouten.add(`De opmaak "${mark.type}" is hier niet toegelaten.`);
      if (mark.type === "link" && !hrefToegelaten(mark.attrs?.["href"])) {
        fouten.add("Een link moet beginnen met /, https://, mailto: of tel:.");
      }
    }
    (node.content ?? []).forEach(loop);
  };
  loop(doc);
  return [...fouten];
}

/** Validates a stored field value (plain text is always valid). */
export function valideerWaarde(waarde: string, profiel: Profiel, veldnaam: string): string[] {
  if (!isDocString(waarde)) return [];
  return valideerDoc(leesRijkeTekst(waarde), profiel).map((fout) => `${veldnaam}: ${fout}`);
}

const BLOK_NODES = new Set([
  "paragraph",
  "heading",
  "blockquote",
  "listItem",
  "tableRow",
  "detailsSummary",
  "detailsContent",
  "horizontalRule",
]);

/** Plain text for meta, JSON-LD, llms.txt and audits. Never contains JSON or markup. */
export function naarPlatteTekst(waarde: string | null | undefined): string {
  const tekst = waarde ?? "";
  if (!isDocString(tekst)) return tekst;
  const delen: string[] = [];
  const loop = (node: RtNode) => {
    if (node.type === "text") delen.push(node.text ?? "");
    else if (node.type === "hardBreak") delen.push("\n");
    else if (node.type === "tableCell" || node.type === "tableHeader") {
      (node.content ?? []).forEach(loop);
      delen.push(" ");
      return;
    } else (node.content ?? []).forEach(loop);
    if (BLOK_NODES.has(node.type)) delen.push("\n\n");
  };
  loop(leesRijkeTekst(tekst));
  return delen
    .join("")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Plain paragraphs, for places that still expect string[]. */
export function naarAlineas(waarde: string): string[] {
  return naarPlatteTekst(waarde)
    .split(/\n\s*\n/)
    .map((deel) => deel.trim())
    .filter(Boolean);
}

type SegmentRef = { node: RtNode; veld: "text" | "alt" | "caption" | "title" };

function segmentRefs(doc: RtNode): SegmentRef[] {
  const refs: SegmentRef[] = [];
  const loop = (node: RtNode) => {
    if (node.type === "text" && (node.text ?? "").trim() !== "") refs.push({ node, veld: "text" });
    if (node.type === "image") {
      if (String(node.attrs?.["alt"] ?? "").trim()) refs.push({ node, veld: "alt" });
      if (String(node.attrs?.["caption"] ?? "").trim()) refs.push({ node, veld: "caption" });
    }
    if (node.type === "youtube" && String(node.attrs?.["title"] ?? "").trim()) refs.push({ node, veld: "title" });
    (node.content ?? []).forEach(loop);
  };
  loop(doc);
  return refs;
}

/** All translatable strings of a document, in document order. */
export function tekstSegmenten(doc: RtDoc): string[] {
  return segmentRefs(doc).map(({ node, veld }) => (veld === "text" ? (node.text ?? "") : String(node.attrs?.[veld] ?? "")));
}

/** Puts translated strings back into a copy of the same structure. Missing segments keep the source text. */
export function zetSegmenten(doc: RtDoc, vertaald: string[]): RtDoc {
  const kopie = JSON.parse(JSON.stringify(doc)) as RtDoc;
  segmentRefs(kopie).forEach(({ node, veld }, index) => {
    const nieuw = vertaald[index];
    if (typeof nieuw !== "string" || nieuw.trim() === "") return;
    if (veld === "text") {
      // Keep leading/trailing spaces of the source so marks stay separated.
      const bron = node.text ?? "";
      const voor = bron.match(/^\s*/)?.[0] ?? "";
      const na = bron.match(/\s*$/)?.[0] ?? "";
      node.text = `${voor}${nieuw.trim()}${na}`;
    } else node.attrs = { ...node.attrs, [veld]: nieuw };
  });
  return kopie;
}

export function tokensIn(tekst: string): string[] {
  return [...tekst.matchAll(/\{(\w+)\}/g)].map((m) => m[1] ?? "").sort();
}

/** Stable FNV-1a hash of a field value, used to compare source versions. */
export function bronHash(waarde: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < waarde.length; i++) {
    hash ^= waarde.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
